/**
 * Protected Route Component
 *
 * Route guard that redirects unauthenticated users to login page.
 * Shows loading spinner while checking authentication state.
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

/**
 * ProtectedRoute wrapper for authenticated-only pages
 *
 * @param children - Components to render if authenticated
 * @returns Children if authenticated, Navigate to /login if not, or loading spinner
 */
export function ProtectedRoute({ children }: ProtectedRouteProps): React.JSX.Element {
  const { currentUser, loading } = useAuth();

  // Show loading spinner while checking auth state
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-green-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated
  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  // Render protected content
  return <>{children}</>;
}
